import { router } from "@inertiajs/react";
import { Autocomplete, Button, MultiSelect, Select, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconLock, IconLockOpen, IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { DocumentTable } from "../../../components/document/general/DocumentTable";
import { FilterSection } from "../../../components/filter/FilterSection";
import { openFile } from "../../../helpers/fileHelper";
import { showDeleteModal } from "../../../helpers/modal";
import { useYearContext } from "../../../hooks/useYearContext";
import { ContentShell } from "../../../layout/ContentShell";
import type { Document } from "../../../models/document";
import type { DocumentType } from "../../../models/enums/documentType";
import { getOccupationOptions, type Occupation } from "../../../models/enums/occupation";
import { Role, roleTranslations } from "../../../models/enums/role";
import { type Group, getGroupOptions } from "../../../models/group";
import type { Pagination } from "../../../models/pagination";
import {
  document_path,
  documents_path,
  edit_document_path,
  new_document_path,
  publish_documents_path,
  unpublish_documents_path,
} from "../../../routes";

type DocumentsProps = {
  documents: Document[];
  groups: Group[];
  apprentice_names: string[];
  apprentice_shorts: string[];
  company_names: string[];
  supervisor_names: string[];
  document_titles: string[];
  filter: FilterFormType & {
    sort_key?: "title" | "year_context" | "occupation" | "group.name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = {
  title?: string;
  apprentice_name?: string;
  apprentice_short?: string;
  company_name?: string;
  supervisor_name?: string;
  group_id?: string;
  occupation?: Occupation;
  document_type?: DocumentType;
  readable?: Role[];
  not_read?: Role[];
  signable?: Role[];
  not_signed?: Role[];
};

const resetFilters: FilterFormType = {
  title: undefined,
  apprentice_name: undefined,
  apprentice_short: undefined,
  company_name: undefined,
  supervisor_name: undefined,
  group_id: undefined,
  occupation: undefined,
};

export default function Documents({
  documents,
  filter,
  document_titles,
  apprentice_names,
  supervisor_names,
  company_names,
  apprentice_shorts,
  groups,
  pagination,
}: DocumentsProps) {
  const { pathWithYearContext } = useYearContext();
  const { sort_key, sort_direction, ...filterFormData } = filter;
  const [selectedRecords, setSelectedRecord] = useState<Document[]>([]);

  const sortStatus: DataTableSortStatus<Document> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const [isFetching, setIsFetching] = useState(false);

  const filterForm = useForm<FilterFormType>({
    mode: "uncontrolled",
    initialValues: {
      ...filterFormData,
    },
  });

  const onNew = () => router.get(pathWithYearContext(new_document_path));
  const onEdit = (document: Document) => router.get(pathWithYearContext(edit_document_path, document.id));
  const onView = (document: Document) => openFile(pathWithYearContext(document_path, document.id));
  const onDelete = (document: Document) =>
    showDeleteModal({
      title: `Globales Dokument ${document.title}`,
      article: "das",
      onConfirm: () => router.delete(pathWithYearContext(document_path, document.id)),
    });

  const handlePageUpdate = (filter: FilterFormType, sortStatus: DataTableSortStatus<Document>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(documents_path), {
      only: ["documents", "filter", "pagination"],
      data: {
        filter: {
          ...filter,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      onFinish: () => setIsFetching(false),
    });
  };
  const onSortChange = (sortStatus: DataTableSortStatus<Document>) => handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const onFilterSubmit = () => handlePageUpdate(filterForm.getValues(), sortStatus, pagination.page);
  const onReset = () => handlePageUpdate(resetFilters, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const onPublish = () => {
    const documentIds = selectedRecords.map((records) => records.id);
    modals.openConfirmModal({
      title: "Dokumente freigeben",
      children: (
        <Text>{`Möchten Sie wirklich ${selectedRecords.length > 1 ? `die ${selectedRecords.length} Dokumente` : "das Dokument"} freigeben`}</Text>
      ),
      labels: { confirm: "Freigeben", cancel: "Abbrechen" },
      confirmProps: { color: "zli-green" },
      onConfirm: () => {
        router.patch(pathWithYearContext(publish_documents_path), { document_ids: documentIds });
        setSelectedRecord([]);
      },
    });
  };

  const onUnpublish = () => {
    const documentIds = selectedRecords.map((records) => records.id);
    modals.openConfirmModal({
      title: "Dokumente zurückziehen",
      children: (
        <Text>{`Möchten Sie wirklich ${selectedRecords.length > 1 ? `die ${selectedRecords.length} Dokumente` : "das Dokument"} zurücknehmen`}</Text>
      ),
      labels: { confirm: "Zurückziehen", cancel: "Abbrechen" },
      confirmProps: { color: "red" },
      onConfirm: () => {
        router.patch(pathWithYearContext(unpublish_documents_path), { document_ids: documentIds });
        setSelectedRecord([]);
      },
    });
  };

  return (
    <ContentShell
      title="Dokumente"
      actions={
        <>
          <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
            Neu
          </Button>
          <Button
            disabled={selectedRecords.length === 0}
            leftSection={<IconLockOpen />}
            color="zli-green"
            variant="light"
            onClick={onPublish}
          >
            Freigeben
          </Button>
          <Button disabled={selectedRecords.length === 0} leftSection={<IconLock />} color="red" variant="light" onClick={onUnpublish}>
            Zurückziehen
          </Button>
        </>
      }
    >
      <FilterSection
        form={filterForm}
        onSubmit={onFilterSubmit}
        onReset={onReset}
        filters={[
          <Autocomplete
            label="Name"
            clearable
            data={apprentice_names}
            key={filterForm.key("apprentice_name")}
            {...filterForm.getInputProps("apprentice_name")}
          />,
          <Autocomplete
            label="Kürzel"
            clearable
            data={apprentice_shorts}
            key={filterForm.key("apprentice_short")}
            {...filterForm.getInputProps("apprentice_short")}
          />,
          <Autocomplete
            label="Lehrbetrieb"
            clearable
            data={company_names}
            key={filterForm.key("company_name")}
            {...filterForm.getInputProps("company_name")}
          />,
          <Autocomplete
            label="Berufsbildner/in"
            clearable
            data={supervisor_names}
            key={filterForm.key("supervisor_name")}
            {...filterForm.getInputProps("supervisor_name")}
          />,
          <Select
            label="Beruf"
            clearable
            data={getOccupationOptions()}
            key={filterForm.key("occupation")}
            {...filterForm.getInputProps("occupation")}
          />,
          <Select
            label="Gruppe"
            clearable
            data={getGroupOptions(groups, false)}
            key={filterForm.key("group_id")}
            {...filterForm.getInputProps("group_id")}
          />,
          <Autocomplete data={document_titles} label="Dokument" key={filterForm.key("title")} {...filterForm.getInputProps("title")} />,
          <MultiSelect
            label="Einsehbar von"
            clearable
            data={roleData}
            key={filterForm.key("readable")}
            {...filterForm.getInputProps("readable")}
          />,
          <MultiSelect
            label="Nicht eingesehen von"
            clearable
            data={roleData}
            key={filterForm.key("not_read")}
            {...filterForm.getInputProps("not_read")}
          />,
          <MultiSelect
            label="Signatur erforderlich von"
            clearable
            data={roleData}
            key={filterForm.key("signable")}
            {...filterForm.getInputProps("signable")}
          />,
          <MultiSelect
            label="Nicht signiert von"
            clearable
            data={roleData}
            key={filterForm.key("not_signed")}
            {...filterForm.getInputProps("not_signed")}
          />,
        ]}
      />
      <DocumentTable
        documents={documents}
        sortStatus={sortStatus}
        onEdit={onEdit}
        onDelete={onDelete}
        onView={onView}
        onSortStatusChange={onSortChange}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={setSelectedRecord}
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </ContentShell>
  );
}

const roleData = [
  { label: roleTranslations[Role.APPRENTICE], value: Role.APPRENTICE },
  { label: roleTranslations[Role.APPRENTICE_SUPERVISOR], value: Role.APPRENTICE_SUPERVISOR },
  { label: roleTranslations[Role.PARENT], value: Role.PARENT },
];
